import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { ClipboardList, CheckCircle, Clock, FileText, Building2, KeyRound, Briefcase, ChevronDown, ChevronUp, AlertTriangle } from"lucide-react";
import { toast } from"sonner";
import { useNavigate } from"react-router-dom";

const ContractorDashboard = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [loading, setLoading] = useState(true);
 const [expandedSections, setExpandedSections] = useState({
 assignments: true,
 units: false,
 offers: false
 });
 const navigate = useNavigate();

 useEffect(() => {
 loadDashboard();
 }, []);

 const loadDashboard = async () => {
 try {
 const response = await axios.get("/dashboard/contractor");
 setDashboard(response.data);
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 };

 const toggleSection = (section) => {
 setExpandedSections(prev => ({
 ...prev,
 [section]: !prev[section]
 }));
 };

 const phaseNames = {
 1: t('projectProgress.phases.phase1'),
 2: t('projectProgress.phases.phase2'),
 3: t('projectProgress.phases.phase3'),
 4: t('projectProgress.phases.phase4')
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 const stats = [
 {
 title: t('contractor.myTasks').replace('My Assigned ', ''),
 value: dashboard?.total_tasks || 0,
 icon: ClipboardList,
 color:"bg-blue-500",
 testId:"total-tasks-stat"
 },
 {
 title: t('tasks.completed'),
 value: dashboard?.completed_tasks || 0,
 icon: CheckCircle,
 color:"bg-green-500",
 testId:"completed-tasks-stat"
 },
 {
 title: t('tasks.inProgress'),
 value: dashboard?.in_progress_tasks || 0,
 icon: Clock,
 color:"bg-orange-500",
 testId:"in-progress-stat"
 },
 {
 title: t('contractor.myOffers'),
 value: dashboard?.offers_count || 0,
 icon: FileText,
 color:"bg-purple-500",
 testId:"offers-stat"
 },
 ];

 const progressPercentage = dashboard?.task_progress_pct ?? (
 dashboard?.total_tasks > 0
 ? Math.round((dashboard.completed_tasks / dashboard.total_tasks) * 100)
 : 0
 );

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="contractor-dashboard"className="space-y-6">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 Welcome, {dashboard?.partner?.company_name || user.name}
 </h1>
 <p className="text-slate-600">
 {dashboard?.partner?.specialization || t('contractorDashboard.subcontractorPortal')}
 </p>
 </div>

 {/* Stats */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 {stats.map((stat) => {
 const Icon = stat.icon;
 return (
 <Card key={stat.title} data-testid={stat.testId} className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stat.value}</p>
 </div>
 <Icon className="h-7 w-7 text-indigo-600"/>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>

 {/* Progress Overview */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center justify-between">
 <span>{t('contractor.myProgress')}</span>
 <Badge variant="outline"className="text-lg px-3 py-1">
 {t('contractorDashboard.percentComplete', { percent: progressPercentage })}
 </Badge>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="w-full bg-slate-200 rounded-full h-4 mb-4">
 <div 
 className="bg-green-500 h-4 rounded-full transition-all duration-500"
 style={{ width: `${progressPercentage}%` }}
 />
 </div>
 <div className="flex justify-between text-sm text-slate-600">
 <span>{progressPercentage}% of work complete across all apartments</span>
 <span>{dashboard?.in_progress_tasks > 0 ? `${dashboard.in_progress_tasks} job(s) in progress` : ""}</span>
 </div>
 </CardContent>
 </Card>

 {/* Quick Actions */}
 <div className="flex gap-4">
 <Button
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 onClick={() => navigate("/my-progress-log")}
 >
 <ClipboardList className="w-4 h-4 mr-2"/>
 {t('contractorDashboard.updateProgress')}
 </Button>
 <Button
 variant="outline"
 onClick={() => navigate("/my-offers")}
 >
 <FileText className="w-4 h-4 mr-2"/>
 {t('contractor.myOffers')}
 </Button>
 </div>

 {/* Assignments */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('assignments')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-blue-600"/>
 {t('contractorDashboard.myAssignments')} ({dashboard?.assignments?.length || 0})
 </CardTitle>
 {expandedSections.assignments ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.assignments && (
 <CardContent>
 {dashboard?.assignments && dashboard.assignments.length > 0 ? (
 <div className="space-y-3">
 {dashboard.assignments.map((assignment) => (
 <div
 key={assignment.id}
 className="p-4 bg-blue-50/80 rounded-sm border border-blue-200/60"
 >
 <div className="flex items-center justify-between">
 <div>
 <h3 className="font-semibold text-slate-900">
 {assignment.project_name ||"Project"}
 </h3>
 <p className="text-sm text-slate-600 mt-1">
 {assignment.service_type}
 </p>
 {assignment.phase && (
 <Badge variant="outline"className="mt-2 bg-blue-500/10 text-blue-700">
 {t('contractorDashboard.phase')} {assignment.phase} – {phaseNames[assignment.phase]}
 </Badge>
 )}
 {/* Deadline display */}
 {assignment.job_deadline && (() => {
 const dl = new Date(assignment.job_deadline);
 const daysLeft = Math.ceil((dl - Date.now()) / 86400000);
 const isUrgent = daysLeft <= 14;
 const isPast = daysLeft < 0;
 return (
 <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${isPast ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-500'}`}>
 <AlertTriangle className={`w-3 h-3 ${isPast || isUrgent ? 'visible' : 'invisible'}`} />
 {isPast
 ? `Deadline passed ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`
 : daysLeft === 0
 ? 'Deadline is TODAY'
 : `Deadline: ${dl.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} (${daysLeft} days left)`
 }
 </div>
 );
 })()}
 </div>
 <div className="text-right">
 <p className="text-lg font-semibold text-slate-900">
 €{assignment.contract_value?.toLocaleString() || 0}
 </p>
 <p className="text-[11px] text-slate-500">{t('contractor.contractValue')}</p>
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">{t('contractorDashboard.noAssignments')}</p>
 )}
 </CardContent>
 )}
 </Card>

 {/* Units (without status) */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('units')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-purple-600"/>
 {t('contractorDashboard.projectUnits')} ({dashboard?.units_count || 0})
 </CardTitle>
 {expandedSections.units ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.units && (
 <CardContent>
 {dashboard?.units && dashboard.units.length > 0 ? (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
 {dashboard.units.slice(0, 12).map((unit) => (
 <Card key={unit.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center gap-2">
 {unit.unit_type === 'commercial'
 ? <Briefcase className="w-5 h-5 text-blue-600"/>
 : <KeyRound className="w-5 h-5 text-purple-600"/>
 }
 <div>
 <p className="font-bold text-slate-900">{unit.code}</p>
 <p className="text-[11px] text-slate-500">Floor {unit.floor || "N/A"}</p>
 </div>
 </div>
 </div>
 <div className="space-y-1 text-sm">
 <div className="flex justify-between">
 <span className="text-sm text-slate-500">Bedrooms:</span>
 <span className="font-medium text-slate-900">{unit.bedrooms ?? "—"}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-slate-500">Area:</span>
 <span className="font-medium text-slate-900">{unit.net_m2 ?? unit.area ?? "—"} m²</span>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">{t('contractorDashboard.noUnitsFound')}</p>
 )}
 {dashboard?.units_count > 12 && (
 <p className="text-sm text-slate-500 text-center mt-3">
 {t('contractorDashboard.showingOf', { shown: 12, total: dashboard.units_count })}
 </p>
 )}
 </CardContent>
 )}
 </Card>

 {/* My Offers */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('offers')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <FileText className="w-5 h-5 text-purple-500"/>
 {t('contractor.myOffers')} ({dashboard?.offers_count || 0})
 </CardTitle>
 {expandedSections.offers ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.offers && (
 <CardContent>
 {dashboard?.my_offers && dashboard.my_offers.length > 0 ? (
 <div className="space-y-2">
 {dashboard.my_offers.map((offer) => (
 <div
 key={offer.id}
 className="flex items-center justify-between p-3 bg-white/50 rounded-sm border border-slate-200"
 >
 <div>
 <p className="font-medium text-slate-900">{offer.service_type}</p>
 <p className="text-[11px] text-slate-500">{offer.description?.slice(0, 50)}...</p>
 </div>
 <div className="flex items-center gap-3">
 <p className="font-semibold text-[#334155]">€{offer.amount?.toLocaleString()}</p>
 <Badge 
 variant="outline"
 className={
 offer.status ==="accepted"?"bg-emerald-500/10 text-emerald-700":
 offer.status ==="rejected"?"bg-rose-500/10 text-rose-700":
"bg-amber-500/10 text-amber-700"
 }
 >
 {offer.status}
 </Badge>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-4">
 <p className="text-sm text-slate-500 mb-3">{t('contractorDashboard.noOffersYet')}</p>
 <Button size="sm"onClick={() => navigate("/my-offers")}>
 {t('contractorDashboard.createNewOffer')}
 </Button>
 </div>
 )}
 </CardContent>
 )}
 </Card>
 </div>
 </Layout>
 );
};

export default ContractorDashboard;
